/**
 * Reusable fields and option lists for lead form schemas.
 *
 * A field's `crm` is the Zoho Lead API name (or label) its value lands in.
 * Anything without `crm`, or that Zoho can't place, is kept in the Lead's
 * Note — so adding a question never loses data.
 */
import { splitName } from "@/forms/engine/mapping";

// ─── Contact basics ────────────────────────────────────────────────────

export const firstName = {
  name: "first_name", type: "text", label: "First name", required: true, errorName: "your first name",
  crm: "First_Name", autoComplete: "given-name",
};
export const lastName = {
  name: "last_name", type: "text", label: "Last name", required: true, errorName: "your last name",
  crm: "Last_Name", autoComplete: "family-name",
};
export const fullName = {
  name: "full_name", type: "text", label: "Your name", required: true, errorName: "your name",
  crm: (value) => splitName(value), autoComplete: "name",
};
export const email = {
  name: "email", type: "email", label: "Email", required: true, errorName: "your email address",
  crm: "Email",
};
export const phone = {
  name: "phone", type: "tel", label: "Phone", crm: "Phone",
};
export const phoneRequired = { ...phone, required: true, errorName: "your phone number" };
export const company = {
  name: "company", type: "text", label: "Business name", crm: "Company", autoComplete: "organization",
};
export const companyRequired = { ...company, required: true, errorName: "your business name" };
export const website = {
  name: "business_website", type: "url", label: "Website", crm: "Website", autoComplete: "url",
  placeholder: "https://",
};
export const message = {
  name: "message", type: "textarea", label: "How can we help?", crm: "Description", maxLength: 2000,
  placeholder: "Tell us about your business and what you're looking for...",
};

// ─── Qualification ─────────────────────────────────────────────────────

/** Zoho `Needs` picklist values: Card machine, EPOS, Pay by link,
 *  Telephone payments, Online payments, Merchant cash advance. */
export const NEEDS_OPTIONS = [
  { value: "card_machine", label: "Card machines", crm: "Card machine" },
  { value: "epos", label: "EPOS / till system", crm: "EPOS" },
  { value: "online", label: "Online payments", crm: "Online payments" },
  { value: "pay_by_link", label: "Pay by link", crm: "Pay by link" },
  { value: "phone", label: "Phone payments", crm: "Telephone payments" },
  { value: "funding", label: "Business funding", crm: "Merchant cash advance" },
  { value: "lower_fees", label: "Lower my fees", crm: null },
];

export const needs = {
  name: "needs", type: "chips", label: "What can we help with?", answerLabel: "Interested in",
  options: NEEDS_OPTIONS, crm: "Needs",
};

/** Same option set as Needs, for How_do_you_take_payments_now. */
export const PAYMENT_METHOD_OPTIONS = NEEDS_OPTIONS.filter((o) => o.crm);

export const currentProvider = {
  name: "current_provider", type: "text", label: "Current payment provider",
  placeholder: "e.g. Worldpay, Square, Stripe", crm: "Current_Supplier",
};

export const TURNOVER_BANDS = [
  { value: "under_10k", label: "Under £10,000", midpoint: 5000 },
  { value: "10k_30k", label: "£10,000 – £30,000", midpoint: 20000 },
  { value: "30k_75k", label: "£30,000 – £75,000", midpoint: 50000 },
  { value: "75k_plus", label: "£75,000+", midpoint: 100000 },
];

/** Band question. The CRM stores a currency, so the band's midpoint is sent
 *  to Monthly_Turnover and the band itself is kept in the Note. */
export const turnoverBand = {
  name: "turnover_band", type: "select", label: "Monthly card turnover",
  options: TURNOVER_BANDS.map(({ value, label }) => ({ value, label })),
  crm: (value) => {
    const band = TURNOVER_BANDS.find((b) => b.value === value);
    return band ? { Monthly_Turnover: band.midpoint } : {};
  },
};

export const CONTRACT_STATUS_OPTIONS = [
  { value: "out_of_contract", label: "Out of contract", crm: "Out of contract" },
  { value: "less_than_6", label: "Less than 6 months left", crm: "Less than 6 months left" },
  { value: "six_to_twelve", label: "6–12 months left", crm: "6-12 months left" },
  { value: "over_twelve", label: "More than 12 months left", crm: "More than 12 months left" },
  { value: "not_sure", label: "Not sure", crm: "Not sure" },
];

export const contractStatus = {
  name: "contract_status", type: "select", label: "Where are you in your current contract?",
  options: CONTRACT_STATUS_OPTIONS, crm: "Contract_Status",
};

export const LOCATION_OPTIONS = [
  { value: "1", label: "1" },
  { value: "2-3", label: "2–3", crm: "2-3" },
  { value: "4-10", label: "4–10", crm: "4-10" },
  { value: "10+", label: "10+" },
];

export const locations = {
  name: "locations", type: "radio", label: "Number of locations", options: LOCATION_OPTIONS,
  crm: "Number_of_Locations",
};

// ─── Shared UI ─────────────────────────────────────────────────────────

/** Reply promise + phone fallback shown under the submit button. */
export const replyFooter = (
  <div className="text-center text-xs text-brand-grey pt-1" data-testid="lead-form-reassurance">
    <p>
      <span className="font-semibold text-brand-black/80">Reply in under 2 business hours.</span>
      {/* One string: adjacent text nodes break hydration under react-snap (#418). */}
      {" Prefer to talk? "}
      <a href="tel:+448003682989" className="font-semibold text-brand-aqua-dark hover:text-brand-black transition-colors">
        Call 0800 368 2989
      </a>
    </p>
  </div>
);
